import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import {
  Add,
  Archive,
  CheckCircleOutline,
  Edit,
  Unarchive,
} from '@mui/icons-material';
import { Formik, FormikHelpers } from 'formik';
import {
  Timestamp,
  WriteBatch,
  deleteField,
  doc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {
  attendeesCollectionRef,
  eventsCollectionRef,
  groupsCollectionRef,
  rotationsCollectionRef,
} from '../../lib/firebase';
import { useFirebaseContext } from '../../lib/firebase-context';
import { useToast } from '../../lib/toast-context';
import { AppEvent } from '../../lib/types';
import {
  EVENT_NAME_MAX_LENGTH,
  FIRESTORE_BATCH_LIMIT,
} from '../../lib/constants';
import { parseLocalNoon } from '../../lib/dates';
import {
  activateEvent,
  archiveEvent,
  bulkClearLabelUrlsForEvent,
  createEvent,
  unarchiveEvent,
  updateEvent,
  useActor,
} from '../../lib/audited-writes';
import { hasCachedLabel } from '../../lib/writes/label-cache';
import {
  EventFormValues,
  blankEventFormValues,
  buildEventSchema,
  formatEventDates,
  timestampToInputValue,
} from './manage-events.utils';

// Stages clone operations onto an existing `WriteBatch` (does not commit).
// Letting the caller own the batch is what makes "create new event + copy
// children" atomic — if any single op in the merged batch fails, no event
// doc and no children get written.
//
// Pre-generates rotation IDs so we can remap `Group.rotationRef` from source
// refs to their new counterparts within the same batch.
const stageCloneEventChildren = async (
  batch: WriteBatch,
  reservedOps: number,
  sourceEventRef: string,
  newEventRef: string,
): Promise<void> => {
  const [rotationSnap, groupSnap] = await Promise.all([
    getDocs(
      query(rotationsCollectionRef, where('eventRef', '==', sourceEventRef)),
    ),
    getDocs(
      query(groupsCollectionRef, where('eventRef', '==', sourceEventRef)),
    ),
  ]);

  const totalOps =
    reservedOps + rotationSnap.size + groupSnap.size;
  if (totalOps > FIRESTORE_BATCH_LIMIT) {
    throw new Error(
      `Too many groups/rotations to copy in a single batch (${totalOps}). Split required.`,
    );
  }

  const rotationRefMap = new Map<string, string>();
  rotationSnap.forEach((r) => {
    rotationRefMap.set(r.id, doc(rotationsCollectionRef).id);
  });

  rotationSnap.forEach((r) => {
    const newRef = rotationRefMap.get(r.id)!;
    const data = r.data();
    batch.set(doc(rotationsCollectionRef, newRef), {
      name: data.name,
      eventRef: newEventRef,
    });
  });

  groupSnap.forEach((g) => {
    const data = g.data();
    const payload: Record<string, unknown> = {
      eventRef: newEventRef,
      grade: data.grade,
      groupName: data.groupName,
      teachers: data.teachers,
    };
    if (data.rotationRef && rotationRefMap.has(data.rotationRef)) {
      payload.rotationRef = rotationRefMap.get(data.rotationRef);
    }
    batch.set(doc(groupsCollectionRef), payload);
  });
};

const EventFormDialog = ({
  open,
  event,
  events,
  onClose,
  onSaved,
  onError,
}: {
  open: boolean;
  event: AppEvent | null;
  events: AppEvent[];
  onClose: () => void;
  onSaved: (mode: 'add' | 'edit') => void;
  onError: (msg: string) => void;
}) => {
  const isEdit = event !== null;
  const actor = useActor();
  const [isWorking, setIsWorking] = useState(false);

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  const initialValues = useMemo<EventFormValues>(
    () =>
      event
        ? {
            name: event.name,
            startDate: timestampToInputValue(event.startDate),
            endDate: timestampToInputValue(event.endDate),
            // Legacy events have no `printGrade` field. Treat absence as
            // `false` so the toggle reflects the effective behavior on
            // the wire (see `buildCreateLabelPdfBody`).
            printGrade: event.printGrade ?? false,
            hideCheckout: event.hideCheckout ?? false,
            foldGradeIntoGroupName: event.foldGradeIntoGroupName ?? false,
            copyFromRef: '',
          }
        : blankEventFormValues,
    [event],
  );

  const validationSchema = useMemo(
    () => buildEventSchema(events, event?.ref),
    [events, event?.ref],
  );

  const copySources = useMemo(
    () => events.filter((e) => e.ref !== event?.ref),
    [events, event?.ref],
  );

  const handleSubmit = async (
    values: EventFormValues,
    { setSubmitting }: FormikHelpers<EventFormValues>,
  ) => {
    setIsWorking(true);
    let succeeded = false;
    try {
      const start = values.startDate
        ? parseLocalNoon(values.startDate)
        : null;
      const end = values.endDate ? parseLocalNoon(values.endDate) : null;

      if (event) {
        // Edit: only metadata. isActive / isArchived are controlled elsewhere.
        // Both of these change what the printed badge looks like.
        const labelSettingChanged =
          (event.printGrade ?? false) !== values.printGrade ||
          (event.foldGradeIntoGroupName ?? false) !==
            values.foldGradeIntoGroupName;
        await updateEvent(actor, event, {
          name: values.name.trim(),
          startDate: start ? Timestamp.fromDate(start) : deleteField(),
          endDate: end ? Timestamp.fromDate(end) : deleteField(),
          printGrade: values.printGrade,
          hideCheckout: values.hideCheckout,
          foldGradeIntoGroupName: values.foldGradeIntoGroupName,
        });
        // Toggling a badge-layout setting makes every cached `s3LabelUrl`
        // for this event stale. Clear them in the same flow rather than
        // relying on the admin to remember the Admin → Printers "Clear
        // cached labels" button. Failures are non-fatal — the save already
        // committed and the admin can re-run the manual clear if this
        // errors.
        if (labelSettingChanged) {
          try {
            const snap = await getDocs(
              query(attendeesCollectionRef, where('eventRef', '==', event.ref)),
            );
            const refs = snap.docs
              .filter((d) =>
                hasCachedLabel(
                  (d.data() as { s3LabelUrl?: unknown }).s3LabelUrl,
                ),
              )
              .map((d) => d.id);
            if (refs.length > 0) {
              await bulkClearLabelUrlsForEvent(actor, event, refs);
            }
          } catch (clearErr) {
            console.error(
              'Failed to clear cached labels after badge-setting toggle',
              clearErr,
            );
          }
        }
      } else {
        const newRef = doc(eventsCollectionRef).id;
        // The very first event auto-activates so an admin doesn't have to
        // click Activate as a separate step on a fresh install / wipe.
        const isFirstEvent = events.length === 0;
        const createPayload: Record<string, unknown> = {
          name: values.name.trim(),
          isActive: isFirstEvent,
          printGrade: values.printGrade,
          hideCheckout: values.hideCheckout,
          foldGradeIntoGroupName: values.foldGradeIntoGroupName,
        };
        if (start) createPayload.startDate = Timestamp.fromDate(start);
        if (end) createPayload.endDate = Timestamp.fromDate(end);

        await createEvent(
          actor,
          newRef,
          createPayload,
          values.copyFromRef
            ? (batch) =>
                stageCloneEventChildren(batch, 1, values.copyFromRef, newRef)
            : undefined,
        );
      }
      succeeded = true;
    } catch (error) {
      console.error('Error saving event', error);
      onError(isEdit ? 'Failed to update event' : 'Failed to add event');
    } finally {
      setSubmitting(false);
      setIsWorking(false);
    }
    if (succeeded) {
      onSaved(isEdit ? 'edit' : 'add');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>{isEdit ? 'Edit event' : 'Add event'}</DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Stack spacing={2}>
                <TextField
                  name='name'
                  label='Event name'
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={
                    (touched.name && errors.name) ||
                    `${values.name.length}/${EVENT_NAME_MAX_LENGTH}`
                  }
                  inputProps={{ maxLength: EVENT_NAME_MAX_LENGTH }}
                  disabled={isSubmitting}
                  fullWidth
                  autoFocus
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    name='startDate'
                    label='Start date (optional)'
                    type='date'
                    value={values.startDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.startDate && Boolean(errors.startDate)}
                    helperText={touched.startDate && (errors.startDate as string)}
                    disabled={isSubmitting}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      '& input::-webkit-calendar-picker-indicator': {
                        filter: 'invert(1)',
                        cursor: 'pointer',
                      },
                    }}
                  />
                  <TextField
                    name='endDate'
                    label='End date (optional)'
                    type='date'
                    value={values.endDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.endDate && Boolean(errors.endDate)}
                    helperText={touched.endDate && (errors.endDate as string)}
                    disabled={isSubmitting}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      '& input::-webkit-calendar-picker-indicator': {
                        filter: 'invert(1)',
                        cursor: 'pointer',
                      },
                    }}
                  />
                </Stack>
                <FormControl disabled={isSubmitting}>
                  <FormControlLabel
                    control={
                      <Switch
                        name='printGrade'
                        checked={values.printGrade}
                        onChange={handleChange}
                      />
                    }
                    label='Print grade on label'
                  />
                  <FormHelperText>
                    When off, the badge omits the grade chip. Toggling this
                    on an existing event clears every cached label so the
                    next print regenerates with the new setting.
                  </FormHelperText>
                </FormControl>
                <FormControl disabled={isSubmitting}>
                  <FormControlLabel
                    control={
                      <Switch
                        name='foldGradeIntoGroupName'
                        checked={values.foldGradeIntoGroupName}
                        onChange={handleChange}
                      />
                    }
                    label='Fold grade into class name'
                  />
                  <FormHelperText>
                    When on, the badge&apos;s class line carries the grade as
                    a prefix (&quot;3rd A - Room 207&quot;) instead of a
                    separate, smaller grade line below it — which also makes
                    the &quot;Print grade on label&quot; setting above
                    irrelevant for this event. Toggling this on an existing
                    event clears every cached label so the next print
                    regenerates with the new layout.
                  </FormHelperText>
                </FormControl>
                <FormControl disabled={isSubmitting}>
                  <FormControlLabel
                    control={
                      <Switch
                        name='hideCheckout'
                        checked={values.hideCheckout}
                        onChange={handleChange}
                      />
                    }
                    label='Hide check-out'
                  />
                  <FormHelperText>
                    When on, volunteers only check kids in — the Check out
                    button, the Checked Out counter, and the Out stat are
                    hidden for this event. For programs that don&apos;t
                    track departures.
                  </FormHelperText>
                </FormControl>
                {!isEdit && copySources.length > 0 && (
                  <FormControl fullWidth disabled={isSubmitting}>
                    <InputLabel id='copy-from-label'>
                      Copy groups + rotations from
                    </InputLabel>
                    <Select
                      labelId='copy-from-label'
                      name='copyFromRef'
                      value={values.copyFromRef}
                      label='Copy groups + rotations from'
                      onChange={
                        handleChange as unknown as (
                          e: SelectChangeEvent<string>,
                        ) => void
                      }
                    >
                      <MenuItem value=''>
                        <em>— Blank event —</em>
                      </MenuItem>
                      {copySources.map((e) => (
                        <MenuItem key={e.ref} value={e.ref}>
                          {e.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type='submit'
                variant='contained'
                disabled={isSubmitting}
              >
                {isEdit ? 'Save' : 'Add event'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

const ArchiveEventDialog = ({
  event,
  mode,
  onClose,
  onDone,
  onError,
}: {
  event: AppEvent | null;
  mode: 'archive' | 'unarchive';
  onClose: () => void;
  onDone: (mode: 'archive' | 'unarchive') => void;
  onError: (msg: string) => void;
}) => {
  const actor = useActor();
  const [isWorking, setIsWorking] = useState(false);

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!event) return;
    setIsWorking(true);
    try {
      if (mode === 'archive') {
        await archiveEvent(actor, event);
      } else {
        await unarchiveEvent(actor, event);
      }
      onDone(mode);
      onClose();
    } catch (error) {
      console.error('Error toggling archive', error);
      onError(
        mode === 'archive'
          ? 'Failed to archive event'
          : 'Failed to unarchive event',
      );
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Dialog open={Boolean(event)} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        {mode === 'archive' ? 'Archive event' : 'Unarchive event'}
      </DialogTitle>
      <DialogContent>
        {event && (
          <DialogContentText>
            {mode === 'archive' ? (
              <>
                Archive <strong>{event.name}</strong>? Its data is preserved
                and the event stays editable, but it disappears from the
                active-event picker.
              </>
            ) : (
              <>
                Unarchive <strong>{event.name}</strong>? It will reappear in
                the active-event picker.
              </>
            )}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isWorking}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant='contained'
          color={mode === 'archive' ? 'warning' : 'primary'}
          disabled={isWorking}
        >
          {mode === 'archive' ? 'Archive' : 'Unarchive'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ManageEvents = () => {
  const { events, activeEvent } = useFirebaseContext();
  const toast = useToast();
  const actor = useActor();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AppEvent | null>(null);
  const [archiving, setArchiving] = useState<AppEvent | null>(null);
  const [unarchiving, setUnarchiving] = useState<AppEvent | null>(null);
  // Tracks the event ref currently being activated so we can disable its
  // button (and the others) until the batched write resolves. Prevents
  // double-clicks from firing two demote/promote pairs.
  const [activatingRef, setActivatingRef] = useState<string | null>(null);

  const sortedEvents = useMemo(
    () =>
      [...events].sort((a, b) => {
        // Active first, then non-archived, then by name.
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        const aArch = Boolean(a.isArchived);
        const bArch = Boolean(b.isArchived);
        if (aArch !== bArch) return aArch ? 1 : -1;
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      }),
    [events],
  );

  const handleActivate = async (target: AppEvent) => {
    if (target.isActive) return;
    if (target.isArchived) {
      toast.error('Unarchive the event before activating it');
      return;
    }
    if (activatingRef) return;
    setActivatingRef(target.ref);
    try {
      await activateEvent(actor, target, activeEvent ?? null);
      toast.success(`"${target.name}" is now active`);
    } catch (error) {
      console.error('Failed to activate event', error);
      toast.error('Failed to activate event');
    } finally {
      setActivatingRef(null);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant='contained'
          startIcon={<Add />}
          onClick={() => setAdding(true)}
        >
          Add event
        </Button>
      </Box>

      {events.length === 0 && (
        <Typography color='text.secondary' textAlign='center' sx={{ py: 2 }}>
          No events yet. Click <strong>Add event</strong> to create one. The
          first event you add becomes active automatically.
        </Typography>
      )}

      <List dense disablePadding>
        {sortedEvents.map((event) => {
          const isActive = event.isActive;
          const isArchived = Boolean(event.isArchived);
          const dateRange = formatEventDates(event);
          const isActivating = activatingRef === event.ref;
          return (
            <ListItem
              key={event.ref}
              divider
              sx={{
                py: 0.5,
                px: 1,
                gap: 1,
                opacity: isArchived ? 0.6 : 1,
                '&:last-child': { borderBottom: 0 },
              }}
            >
              <Box
                sx={{
                  width: 24,
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                {isActive && (
                  <CheckCircleOutline color='success' fontSize='small' />
                )}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 500 }}
                    noWrap
                  >
                    {event.name}
                  </Typography>
                  {isActive && (
                    <Chip
                      size='small'
                      color='success'
                      variant='outlined'
                      label='Active'
                    />
                  )}
                  {isArchived && (
                    <Chip
                      size='small'
                      color='default'
                      variant='outlined'
                      label='Archived'
                    />
                  )}
                </Box>
                {dateRange && (
                  <Typography variant='caption' color='text.secondary'>
                    {dateRange}
                  </Typography>
                )}
              </Box>
              {!isActive && !isArchived && (
                <Button
                  size='small'
                  color='success'
                  onClick={() => handleActivate(event)}
                  disabled={Boolean(activatingRef)}
                  sx={{ minWidth: 96 }}
                >
                  {isActivating ? 'Activating…' : 'Activate'}
                </Button>
              )}
              <IconButton
                size='small'
                aria-label={`Edit ${event.name}`}
                onClick={() => setEditing(event)}
              >
                <Edit fontSize='small' />
              </IconButton>
              {isArchived ? (
                <IconButton
                  size='small'
                  aria-label={`Unarchive ${event.name}`}
                  title='Unarchive'
                  onClick={() => setUnarchiving(event)}
                >
                  <Unarchive fontSize='small' />
                </IconButton>
              ) : (
                <IconButton
                  size='small'
                  aria-label={`Archive ${event.name}`}
                  title={
                    isActive
                      ? 'Activate a different event before archiving this one'
                      : 'Archive'
                  }
                  onClick={() => setArchiving(event)}
                  disabled={isActive}
                >
                  <Archive fontSize='small' />
                </IconButton>
              )}
            </ListItem>
          );
        })}
      </List>

      <EventFormDialog
        open={adding || editing !== null}
        event={editing}
        events={events}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={(mode) =>
          toast.success(mode === 'add' ? 'Event added' : 'Event saved')
        }
        onError={(msg) => toast.error(msg)}
      />

      <ArchiveEventDialog
        event={archiving}
        mode='archive'
        onClose={() => setArchiving(null)}
        onDone={() => toast.success('Event archived')}
        onError={(msg) => toast.error(msg)}
      />
      <ArchiveEventDialog
        event={unarchiving}
        mode='unarchive'
        onClose={() => setUnarchiving(null)}
        onDone={() => toast.success('Event unarchived')}
        onError={(msg) => toast.error(msg)}
      />
    </>
  );
};

export default ManageEvents;
